import type { ErrorLog } from '@/types/api.types'
import * as Sentry from '@sentry/vue'
import { sanitizeLoggedUrl } from '@/utils/sanitizeUrl'

/**
 * Normalize an unknown thrown/rejected value into an Error with a useful message.
 * api.ts's response interceptor rejects with plain ApiError objects ({message, status,
 * errors}), not Error instances, so an uncaught rejection's `.reason` is often such an
 * object — stringifying it directly yields the useless "[object Object]".
 */
export function toLoggableError(reason: unknown): Error {
  if (reason instanceof Error) return reason
  if (reason && typeof reason === 'object') {
    const message = (reason as Record<string, unknown>).message
    if (typeof message === 'string' && message) {
      return new Error(message)
    }
    try {
      return new Error(JSON.stringify(reason))
    } catch {
      // falls through to String(reason) below
    }
  }
  return new Error(String(reason))
}

interface LogContext {
  user?: {
    id: number
    username: string
  }
  route?: string
  action?: string
  timestamp: string
  userAgent: string
  url: string
  endpoint?: string
  planLimit?: string
  planUsed?: number
  planMax?: number
}

class LoggerService {
  private errorQueue: ErrorLog[] = []
  private readonly maxQueueSize = 50
  // Must match erp-api's _MAX_ERRORS_PER_REQUEST (app/api/v1/logs.py) — the backend
  // answers 422 for any larger batch, so the queue has to be drained in chunks.
  private readonly maxErrorsPerRequest = 20
  private readonly flushInterval = 30000 // 30 seconds
  private flushTimer: number | null = null

  constructor() {
    if (typeof window !== 'undefined') {
      this.startFlushTimer()
      window.addEventListener('beforeunload', () => this.flush())
    }
  }

  private startFlushTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
    }
    
    this.flushTimer = window.setInterval(() => {
      this.flush()
    }, this.flushInterval)
  }

  private async getContext(action?: string): Promise<LogContext> {
    const user = await this.getUserInfo()
    const hasWindow = typeof window !== 'undefined'
    return {
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      url: hasWindow ? sanitizeLoggedUrl(window.location.href) : '',
      route: hasWindow ? window.location.pathname : '',
      action,
      ...(user ? { user } : {})
    }
  }

  private async getUserInfo() {
    try {
      const { useAuthStore } = await import('@/stores/auth')
      const authStore = useAuthStore()
      if (authStore.user) {
        return {
          id: authStore.user.id,
          username: authStore.user.username || authStore.user.email || ''
        }
      }
    } catch {
      // Store not available, return null
    }
    return null
  }

  /**
   * Backend-owned 5xx — queue for logs but do not duplicate in Sentry.
   * api_network_error fires whenever a request gets no response at all, which includes
   * routine client connectivity loss (mobile network handoffs, backgrounded tabs) as well
   * as requests aborted by the app's own hard redirects (see redirectToLogin in api.ts).
   * pwa_update_check_error fires when the browser's periodic ServiceWorker update check
   * (registration.update(), see PwaUpdateBanner.vue) fails to fetch /sw.js — e.g. racing a
   * deploy rollover. None of that is an actionable code defect, so keep it out of Sentry to
   * avoid alert fatigue — it still reaches the backend via the error queue for aggregate
   * visibility.
   */
  private shouldReportToSentry(action?: string): boolean {
    if (!action) {
      return true
    }
    return (
      action !== 'api_server_error' &&
      action !== 'api_unknown_error' &&
      action !== 'api_network_error' &&
      action !== 'pwa_update_check_error'
    )
  }

  private captureToSentry(error: Error, errorLog: ErrorLog): void {
    if (!import.meta.env.VITE_SENTRY_DSN || !this.shouldReportToSentry(errorLog.action)) {
      return
    }

    try {
      const tags: Record<string, string> = {}
      if (errorLog.action) {
        tags.action = errorLog.action
      }
      if (errorLog.route) {
        tags.route = errorLog.route
      }
      if (errorLog.endpoint) {
        tags.endpoint = errorLog.endpoint
      }
      if (errorLog.planLimit) {
        tags.plan_limit = errorLog.planLimit
      }

      const extra: Record<string, string | number> = {
        timestamp: errorLog.timestamp,
        url: errorLog.url,
        user_agent: errorLog.userAgent
      }
      if (errorLog.planUsed !== undefined) {
        extra.plan_used = errorLog.planUsed
      }
      if (errorLog.planMax !== undefined) {
        extra.plan_max = errorLog.planMax
      }

      Sentry.captureException(error, {
        tags,
        extra,
        ...(errorLog.user ? { user: { id: String(errorLog.user.id) } } : {})
      })
    } catch (sentryError) {
      console.error('Sentry failed to capture exception:', sentryError, 'Original error:', error)
    }
  }

  async logError(error: Error, context?: Partial<LogContext>) {
    const baseContext = await this.getContext(context?.action)
    const errorLog: ErrorLog = {
      message: error.message,
      stack: error.stack || '',
      name: error.name,
      ...baseContext,
      ...context
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('Error logged:', errorLog)
    }

    this.captureToSentry(error, errorLog)

    // Add to queue
    this.errorQueue.push(errorLog)

    // Flush if queue is full
    if (this.errorQueue.length >= this.maxQueueSize) {
      this.flush()
    }
  }

  async logWarning(message: string, context?: Partial<LogContext>) {
    if (import.meta.env.DEV) {
      console.warn('Warning:', message, context)
    }

    // In production, warnings can be sent to backend if needed
  }

  async logInfo(message: string, context?: Partial<LogContext>) {
    if (import.meta.env.DEV) {
      console.info('Info:', message, context)
    }
  }

  async flush() {
    if (this.errorQueue.length === 0) {
      return
    }

    // Take one batch at a time rather than pre-slicing the whole queue: a rejected batch
    // is put back at the head of the queue, and pre-slicing would keep iterating over the
    // already-computed batches and re-ship those re-queued errors in the same flush.
    while (this.errorQueue.length > 0) {
      const batch = this.errorQueue.splice(0, this.maxErrorsPerRequest)

      try {
        // Send errors to backend logging endpoint
        if (import.meta.env.PROD) {
          // Dynamically import api to avoid circular dependency
          const { default: api } = await import('@/services/api')
          // skipErrorLogging: a failed log-shipping request must not log itself back into
          // the queue it's trying to drain — during an outage that turned this into an
          // ever-growing, self-feeding retry batch (see ERP-WEB-K).
          await api.post('/api/v1/logs/errors', { errors: batch }, { skipErrorLogging: true })
        }
      } catch (error) {
        // Put the failed batch back in queue (except the last one, to prevent an endless
        // retry of a request the backend keeps rejecting), ahead of anything queued since.
        this.errorQueue = [...batch.slice(0, -1), ...this.errorQueue]
        console.error('Failed to send error logs:', error)
        // Stop draining: the backend just rejected this batch, so the remaining batches
        // would almost certainly be rejected too. They stay queued for the next flush.
        break
      }
    }
  }

  destroy() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
    this.flush()
  }
}

export const loggerService = new LoggerService()
